import React, {useState, useMemo, forwardRef, useCallback, useEffect, useRef} from 'react';
import {Popover, Button} from 'antd';
import {ExclamationCircleFilled} from '@ant-design/icons';
import useStyle from 'antd/es/popconfirm/style';


/**
 * A PopConfirm using AntD Popover, replicating AntD Popconfirm API with:
 *  - title, icon, description
 *  - okText, cancelText, okButtonProps, cancelButtonProps
 *  - placement, arrow, trigger (triggerAction)
 *  - onConfirm, onCancel
 *  - disabled
 */
const PopConfirm = forwardRef(({
                                   title,
                                   icon = <ExclamationCircleFilled/>,
                                   description,
                                   okText = 'Yes',
                                   cancelText = 'No',
                                   okButtonProps = {},
                                   cancelButtonProps = {},
                                   onConfirm,
                                   onCancel,
                                   onOpenChange,
                                   placement = 'top',
                                   arrow = true,
                                   trigger: triggerAction = 'click',     // renamed to avoid conflict
                                   disabled = false,
                                   children,
                                   ...restPopoverProps
                               }, ref) => {
    const [open, setOpen] = useState(false);
    const [popoverNode, setPopoverNode] = useState(null);

    // Stable ID for aria-* references (dialog container)
    const id = useMemo(() => `popconfirm-${Math.random().toString(36).slice(2, 8)}`, []);

    const cancelRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        const trg = triggerRef.current;
        trg?.removeAttribute?.('aria-describedby');
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setPopoverNode(null);
        onCancel?.();
        triggerRef.current?.focus();
    }, [onCancel]);

    const handleConfirm = useCallback(() => {
        setOpen(false);
        setPopoverNode(null);
        onConfirm?.();
        triggerRef.current?.focus();
    }, [onConfirm]);


    const handleOpenChange = (val => {
        if(onOpenChange != undefined && typeof onOpenChange === 'function') {
            onOpenChange(val);
        }
        setOpen(val);
    });

    useEffect(() => {
        if (open) {
            const wrap = document.getElementById(id)?.closest('.ant-popover');
            if (wrap) setPopoverNode(wrap);
        }
    }, [open, id]);

    // Keyboard & focus-out listeners
    const onKeyDown = useCallback(e => {
        if (e.key === 'Escape') handleClose();
    }, [handleClose]);
    const onFocusOut = useCallback(e => {
        if (popoverNode && !popoverNode.contains(e.target)) handleClose();
    }, [popoverNode, handleClose]);

    useEffect(() => {
        if (popoverNode) {
            cancelRef.current?.focus();
            popoverNode.addEventListener('keydown', onKeyDown);
            //document.addEventListener('focusin', onFocusOut);
            document.addEventListener('click', onFocusOut);
            window.addEventListener('blur', handleClose);
            return () => {
                popoverNode.removeEventListener('keydown', onKeyDown);
                document.removeEventListener('focusin', onFocusOut);
                document.removeEventListener('click', onFocusOut);
                window.removeEventListener('blur', handleClose);
            };
        }
    }, [popoverNode, onKeyDown, onFocusOut, handleClose]);

    const content = (
        <div className="ant-popconfirm ant-popconfirm-inner-content" role="dialog" id={id}>
            <div className="ant-popconfirm-message">
                <span className="ant-popconfirm-message-icon">
                    {icon}
                </span>
                <div className="ant-popconfirm-message-text">
                    <div className="ant-popconfirm-title">
                        <span>{title}</span>
                    </div>
                </div>
            </div>

            {description && (
                <div className="ant-popconfirm-description">
                    {description}
                </div>
            )}

            <div className="ant-popconfirm-buttons">
                <Button ref={cancelRef} size="small" onClick={handleClose} {...cancelButtonProps}>
                    {cancelText}
                </Button>
                <Button size="small" type="primary" onClick={handleConfirm} {...okButtonProps}>
                    {okText}
                </Button>
            </div>
        </div>
    );


    const triggerElement = React.cloneElement(children, {
        ref: node => {
            triggerRef.current = node;
            if (typeof children.ref === 'function') children.ref(node);
        }
    });

    const [wrapSSR] = useStyle('ant-popconfirm');
    return wrapSSR(
        <Popover
            content={content}
            trigger={disabled ? [] : triggerAction}
            placement={placement}
            arrow={arrow}
            open={open}
            onOpenChange={handleOpenChange}
            getPopupContainer={node => node.parentElement || document.body}
            {...restPopoverProps}
        >
            {triggerElement}
        </Popover>
    );
});

export default PopConfirm;